import { createElement, useEffect, useState } from 'react';
import Script from 'next/script';
import { useTheme } from 'styled-components';
import useGameContext from '~/client/components/Game/context/Game/useGameContext';
import { useGetCurrencyRateQueryLazyQuery } from '~/client/components/Promotions/BitcoinPredictor/Banner/Predict/ShowConvertedCurrency/graphql/GetCurrencyRateQuery.generated';
import useCurrencyConfig from '~/client/hooks/currency/useCurrencyConfig';
import useCurrentUser from '~/client/hooks/user/useCurrentUser';
import rateModifier from './rateModifier';
import getGameUrl from '@/actions/getGameUrl';

type Props = {
  gameLaunchUrl: string;
};

const WCContainer: FC<Props> = ({ gameLaunchUrl }) => {
  const [urlParams, setUrlParams] = useState<URLSearchParams>();
  const { options: gameOptions } = useGameContext();
  const theme = useTheme();
  const [fiatOptions, setFiatOptions] = useState({});
  const { profile } = useCurrentUser();
  const currencyList = useCurrencyConfig();
  const walletCurrencyName = currencyList?.name;
  const gameCurrencyName = String(currencyList?.unit);

  const [getCurrencyRate, { data, loading }] =
    useGetCurrencyRateQueryLazyQuery();

  useEffect(() => {
    const profileCurrency = profile?.conversionCurrency;

    if (profileCurrency === undefined || walletCurrencyName === undefined) {
      return;
    }

    // Only can be called when the user is logged in, useEffect below uses
    // the response.
    getCurrencyRate({
      variables: { from: String(walletCurrencyName), to: profileCurrency },
    });
  }, [profile, getCurrencyRate, walletCurrencyName]);

  useEffect(() => {
    const rate = data?.banking?.rate?.rate;

    if (
      rate === undefined ||
      profile === undefined ||
      gameCurrencyName === undefined
    ) {
      return;
    }

    setFiatOptions({
      'fiat-currency': profile?.conversionCurrency,
      'fiat-rate': rate * rateModifier(gameCurrencyName),
    });
  }, [data, loading, profile, setFiatOptions, gameCurrencyName]);

  useEffect(() => {
    if (!gameLaunchUrl) {
      return;
    }
    getGameUrl(gameLaunchUrl)
      .then((url) => {
        if (url) {
          const urlObj = new URL(url);
          setUrlParams(new URLSearchParams(urlObj.search));
        }
      })
      .catch((error) => {
        console.error('Error fetching game URL:', error);
      });
  }, [gameLaunchUrl]);

  if (!gameLaunchUrl || !urlParams) {
    return null;
  }

  const attributes: Record<string, string> = Array.from(
    urlParams.entries(),
  ).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key.replaceAll('_', '-')]: value,
    }),
    {},
  );

  return (
    <div id="wc-game-container" className="absolute h-full w-full">
      {createElement('game-container', {
        className: 'block h-full w-full',
        ...{ theme: theme.colorScheme },
        ...attributes,
        ...gameOptions,
        ...fiatOptions,
      })}
      <Script
        type="module"
        src="https://files.onetouch.io/game/original-games/latest/game-container.js"
        id="wc-game-script"
      />
    </div>
  );
};

export default WCContainer;
